// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { HealthScoreVariant, TableDataRow } from '@lfx/key-projects/key-projects.model';
import { ScoreLevel } from '@lfx/key-projects/entities/base-metric.entity';
import { EcosystemScoreLevel } from '@lfx/key-projects/entities/ecosystem-influence.entity';
import { DropdownMenuItem } from '@lfx/shared/components/dropdown-menu';

/**
 * Entity encapsulating Key Projects Table business logic
 * Follows Clean Architecture: Business rules separated from presentation
 */
export class KeyProjectsTableEntity {
  /**
   * Health score sort order
   * Higher values indicate better health
   */
  private static readonly HEALTH_SCORE_ORDER: Record<HealthScoreVariant, number> = {
    excellent: 5,
    healthy: 4,
    stable: 3,
    unsteady: 2,
    critical: 1,
  } as const;

  /**
   * Technical influence level sort order
   * Used for code-related activities (commits, PRs, maintainers, contributors)
   * Higher values indicate greater influence
   * Type: ScoreLevel ('Leading' | 'Contributing' | 'Participating' | 'Silent')
   */
  private static readonly TECHNICAL_INFLUENCE_ORDER: Record<ScoreLevel, number> = {
    Leading: 4,
    Contributing: 3,
    Participating: 2,
    Silent: 1,
  } as const;

  /**
   * Ecosystem influence level sort order
   * Used for non-code activities (meetings, events, committees, sponsorships)
   * Higher values indicate greater influence
   * Type: EcosystemScoreLevel (extends ScoreLevel with 'Non-LF Project')
   *
   * Note: EcosystemScoreLevel = ScoreLevel | 'Non-LF Project'
   * This includes an additional 'Non-LF Project' level not present in technical influence
   */
  private static readonly ECOSYSTEM_INFLUENCE_ORDER: Record<EcosystemScoreLevel, number> = {
    Leading: 4,
    Contributing: 3,
    Participating: 2,
    Silent: 1,
    'Non-LF Project': 0,
  } as const;

  /**
   * Sort table data by project name
   */
  static sortByProjectName(a: TableDataRow, b: TableDataRow, direction: number): number {
    const nameA = a.projectInfo.name.trim();
    const nameB = b.projectInfo.name.trim();
    return direction * nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  }

  /**
   * Sort table data by health score
   */
  static sortByHealthScore(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (
      KeyProjectsTableEntity.HEALTH_SCORE_ORDER[a.healthScore] -
      KeyProjectsTableEntity.HEALTH_SCORE_ORDER[b.healthScore]
    );
  }

  /**
   * Sort table data by technical influence level (categorical)
   */
  static sortByTechnicalInfluence(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (
      KeyProjectsTableEntity.TECHNICAL_INFLUENCE_ORDER[a.technicalInfluence] -
      KeyProjectsTableEntity.TECHNICAL_INFLUENCE_ORDER[b.technicalInfluence]
    );
  }

  /**
   * Sort table data by technical influence points (numerical)
   */
  static sortByTechnicalInfluencePoints(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (a.technicalInfluencePoints - b.technicalInfluencePoints);
  }

  /**
   * Sort table data by ecosystem influence level (categorical)
   */
  static sortByEcosystemInfluence(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (
      KeyProjectsTableEntity.ECOSYSTEM_INFLUENCE_ORDER[a.ecosystemInfluence] -
      KeyProjectsTableEntity.ECOSYSTEM_INFLUENCE_ORDER[b.ecosystemInfluence]
    );
  }

  /**
   * Sort table data by ecosystem influence points (numerical)
   */
  static sortByEcosystemInfluencePoints(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (a.ecosystemInfluencePoints - b.ecosystemInfluencePoints);
  }

  /**
   * Sort table data by contributor count
   */
  static sortByContributors(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (a.contributors.count - b.contributors.count);
  }

  /**
   * Sort table data by participant count
   */
  static sortByParticipants(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (a.participants.count - b.participants.count);
  }

  /**
   * Sort table data by activity change percentage
   */
  static sortByActivity(a: TableDataRow, b: TableDataRow, direction: number): number {
    return direction * (a.activity.changePercentage - b.activity.changePercentage);
  }

  /**
   * Get action menu items for workspace project operations
   */
  static getActionMenuItems(): DropdownMenuItem[] {
    return [
      {
        id: 'remove-project',
        label: 'Remove project from workspace',
        icon: 'fal fa-layer-minus',
        action: 'remove-project',
        variant: 'danger',
      },
    ];
  }

  /**
   * Get the appropriate sort comparator function based on column property
   */
  static getSortComparator(
    columnProp: string
  ): ((a: TableDataRow, b: TableDataRow, direction: number) => number) | null {
    switch (columnProp) {
      case 'projectInfo':
        return this.sortByProjectName;
      case 'healthScore':
        return this.sortByHealthScore;
      case 'technicalInfluence':
        return this.sortByTechnicalInfluence;
      case 'technicalInfluencePoints':
        return this.sortByTechnicalInfluencePoints;
      case 'ecosystemInfluence':
        return this.sortByEcosystemInfluence;
      case 'ecosystemInfluencePoints':
        return this.sortByEcosystemInfluencePoints;
      case 'contributors':
        return this.sortByContributors;
      case 'participants':
        return this.sortByParticipants;
      case 'activity':
        return this.sortByActivity;
      default:
        return null;
    }
  }
}

